import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { loadRuntimeConfig, RuntimeConfigProvider, type RuntimeConfig } from '@dymaxion/auth';

import { App } from './App';
import './styles.css';

// Opt in to React Router v7 future flags now to silence console
// warnings and get the new startTransition + splat-path behaviour.
const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    },
    mutations: {
      retry: 0,
    },
  },
});

function renderApp(config: RuntimeConfig): void {
  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root element #root not found in index.html');
  }

  // BASE_URL comes from Vite's `base` config (set via VITE_BASE_PATH
  // build arg). React Router needs the same prefix without the
  // trailing slash so client-side navigation works under /login-v2,
  // /login, or any other deployment prefix.
  const routerBasename = stripTrailingSlash(import.meta.env.BASE_URL);

  createRoot(container).render(
    <StrictMode>
      <RuntimeConfigProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={routerBasename} future={routerFutureFlags}>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </RuntimeConfigProvider>
    </StrictMode>,
  );
}

function stripTrailingSlash(s: string): string {
  if (!s || s === '/') return '';
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

function renderConfigError(message: string): void {
  const container = document.getElementById('root');
  if (!container) return;
  // Bare DOM render — no React tree because the runtime config that
  // it depends on couldn't be loaded. This is the loud failure mode
  // for a missing or malformed /config.json.
  container.innerHTML = `
    <div style="
      min-height: 100vh;
      background-color: #000000;
      color: #ffffff;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      font-family: 'IBM Plex Sans', system-ui, sans-serif;
    ">
      <div style="max-width: 560px; text-align: left;">
        <h1 style="font-size: 1.5rem; font-weight: 200; letter-spacing: 0.18em; margin-bottom: 1rem;">
          DYMAXION CONFIG ERROR
        </h1>
        <p style="color: rgba(255,255,255,0.7); font-size: 0.875rem; line-height: 1.6;">
          The login app could not load its host runtime configuration from
          <code style="font-family: monospace; color: #5FE8EC;">/config.json</code>.
          This usually means the per-host config file is not bind-mounted
          into the container correctly.
        </p>
        <pre style="
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          border: 1px solid rgba(255,255,255,0.15);
          background: rgba(255,255,255,0.05);
          border-radius: 0.5rem;
          font-family: monospace;
          font-size: 0.75rem;
          color: #FCA5A5;
          white-space: pre-wrap;
          word-break: break-word;
        ">${message.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c] || c)}</pre>
      </div>
    </div>
  `;
}

// Bootstrap: fetch runtime config FIRST, then render. Any failure
// shows a loud error UI instead of booting with mystery defaults.
// The config URL is resolved against BASE_URL so the SPA can be
// served from any deployment prefix (e.g. /login-v2/config.json).
const configUrl = `${import.meta.env.BASE_URL}config.json`;

loadRuntimeConfig(configUrl)
  .then(renderApp)
  .catch((err: unknown) => {
    // eslint-disable-next-line no-console
    console.error('[main] runtime config failed to load:', err);
    renderConfigError(err instanceof Error ? err.message : String(err));
  });
